/**
 * Plain-language copy for rule codes, verification states and API errors.
 * Members never see a raw code — every code maps to a sentence here.
 */
import { RIDES_ERROR_CODES, RIDES_RULE_CODES, RIDES_SEX, RIDES_UPLOAD_STATUS, RIDES_VERIFICATION_STATUS, CCMSError } from '@actschurch/ccms-sdk';
import { DEFAULT_COPY, fillPlaceholders } from './settings';

export interface RuleCopyContext {
  /** The sex the member declared (RIDES_SEX) — used for the mismatch sentence. */
  declaredSexId?: number | null;
  /** The sex decoded from the document (RIDES_SEX). */
  documentSexId?: number | null;
  /** 'id' | 'passport' | 'licence' */
  document?: 'id' | 'passport' | 'licence';
  /** Allowed SA licence codes (settings.allowedLicenceCodes) — used for the licence-code sentence. */
  allowedCodes?: string[];
}

const sexWord = (id: number | null | undefined) =>
  id === RIDES_SEX.Female ? 'female' : id === RIDES_SEX.Male ? 'male' : 'a different sex';

/** Map a RIDES_RULE_CODES value to a friendly sentence. */
export function ruleCodeCopy(code: string, ctx: RuleCopyContext = {}): string {
  const doc = ctx.document ?? 'id';
  switch (code) {
    case RIDES_RULE_CODES.Format:
      if (doc === 'passport') return 'A passport number is 6 to 9 letters and digits. Please check it against the photo page.';
      if (doc === 'licence') return 'Please check the licence details. Something is missing.';
      return 'An SA ID number is exactly 13 digits. Please check for a missing or extra digit.';
    case RIDES_RULE_CODES.Luhn:
      return "That ID number's check digit doesn't add up. Please re-check the last digit.";
    case RIDES_RULE_CODES.Date:
      return doc === 'id'
        ? "The first six digits (your date of birth) don't make a real date. Please re-check them."
        : 'That date is not a real calendar date. Please re-check it.';
    case RIDES_RULE_CODES.Age:
      return 'That date of birth gives an age that is not possible. Please re-check the number.';
    case RIDES_RULE_CODES.Citizenship:
      return 'The 11th digit should be 0, 1 or 2. Please re-check your ID number.';
    case RIDES_RULE_CODES.RaceDigit:
      return 'The 12th digit should be 8 or 9. Please re-check your ID number.';
    case RIDES_RULE_CODES.DobMismatch:
      return "The date of birth on this document doesn't match the one on your profile. Please fix your profile under Account, or re-check the details.";
    case RIDES_RULE_CODES.DobCorrected:
      return 'We updated the date of birth on your profile to match your ID.';
    case RIDES_RULE_CODES.SexMismatch:
      return `This document is registered as ${sexWord(ctx.documentSexId)} but you chose ${sexWord(ctx.declaredSexId)}. Please check your details.`;
    case RIDES_RULE_CODES.DriverUnder18:
      return 'Drivers must be 18 or older.';
    case RIDES_RULE_CODES.GuardianIsMinor:
      return 'A parent or guardian must be 18 or older.';
    case RIDES_RULE_CODES.Expired:
      return doc === 'licence'
        ? 'This licence has expired. Carpool can only accept drivers with a valid licence.'
        : 'This document has expired. Please use one that is still valid.';
    case RIDES_RULE_CODES.ExpiryRequired:
      return 'Please enter the expiry date printed on the document.';
    case RIDES_RULE_CODES.CountryRequired:
      return 'Please choose the country that issued the document.';
    case RIDES_RULE_CODES.MrzCheckDigit:
      return "The machine-readable line doesn't match the passport number. Leave it blank or re-type it exactly.";
    case RIDES_RULE_CODES.PassportPatternWarning:
      return "This doesn't look like a typical South African passport number. Please double-check it, but you can continue.";
    case RIDES_RULE_CODES.LicenceCode: {
      const codes = ctx.allowedCodes?.length ? ctx.allowedCodes : ['B', 'EB', 'C1', 'EC1', 'C', 'EC'];
      const list = codes.length > 1 ? `${codes.slice(0, -1).join(', ')} or ${codes[codes.length - 1]}` : codes[0];
      return `Carpool needs a car licence: code ${list}.`;
    }
    case RIDES_RULE_CODES.ForeignLicenceNotUsable:
      return 'A foreign licence must be in English with a photo, or come with an International Driving Permit.';
    case RIDES_RULE_CODES.DocumentInUse:
      return 'This document is already linked to another Carpool account. Please contact the church office.';
    default:
      return 'Something about this document could not be checked. Please re-check the details.';
  }
}

/** Friendly sentence for the server's check list (passed/failed/warning). */
export function checkItemCopy(code: string, passed: boolean, warning: boolean, ctx: RuleCopyContext = {}): string {
  if (passed) {
    switch (code) {
      case RIDES_RULE_CODES.Luhn: return 'Check digit is correct';
      case RIDES_RULE_CODES.Date: return 'Date of birth reads as a real date';
      case RIDES_RULE_CODES.DobMismatch: return 'Date of birth matches your profile';
      case RIDES_RULE_CODES.SexMismatch: return 'Sex matches your profile';
      case RIDES_RULE_CODES.Expired: return 'Document is still valid';
      case RIDES_RULE_CODES.LicenceCode: return 'Licence code is allowed';
      case RIDES_RULE_CODES.Format: return 'Format looks right';
      case RIDES_RULE_CODES.MrzCheckDigit: return 'Machine-readable line matches';
      case RIDES_RULE_CODES.DocumentInUse: return 'Not linked to another account';
      case RIDES_RULE_CODES.DriverUnder18: return 'Driver is 18 or older';
      case RIDES_RULE_CODES.GuardianIsMinor: return 'Guardian is 18 or older';
      default: return 'Passed';
    }
  }
  const text = ruleCodeCopy(code, ctx);
  return warning ? `Heads up: ${text}` : text;
}

export type PillKind = 'ok' | 'warn' | 'bad' | 'info';

export interface VerificationStatusCopy {
  label: string;
  kind: PillKind;
  detail: string;
}

/** Pill + explanation for a verification status (Manual provider = physical check at the campus). */
export function verificationStatusCopy(
  statusId: number | null | undefined,
  failureCode?: string | null,
  providerName?: string | null,
): VerificationStatusCopy {
  switch (statusId) {
    case RIDES_VERIFICATION_STATUS.Verified:
      return { label: 'Verified', kind: 'ok', detail: 'Checked and confirmed.' };
    case RIDES_VERIFICATION_STATUS.Pending:
      return {
        label: 'Pending',
        kind: 'warn',
        detail:
          providerName === 'Simulated'
            ? 'Being checked…'
            : 'Waiting for a check at the campus. Please bring the document with you.',
      };
    case RIDES_VERIFICATION_STATUS.Failed:
      return {
        label: 'Failed',
        kind: 'bad',
        detail: failureCode ? ruleCodeCopy(failureCode) : 'The check did not pass. Please try again with the correct details.',
      };
    case RIDES_VERIFICATION_STATUS.Expired:
      return { label: 'Expired', kind: 'bad', detail: 'This document has expired. Please submit a valid one.' };
    case RIDES_VERIFICATION_STATUS.Revoked:
      return { label: 'Revoked', kind: 'bad', detail: 'This verification was withdrawn. Please contact the church office.' };
    default:
      return { label: 'Required', kind: 'bad', detail: 'Not submitted yet.' };
  }
}

/**
 * Pill for a document upload (RIDES_UPLOAD_STATUS). `null` = nothing uploaded yet. While approval
 * is not required a Submitted document already counts, so its pill is calmer ("info").
 */
export function uploadStatusCopy(
  statusId: number | null | undefined,
  opts: { isExpired?: boolean; approvalRequired?: boolean; required?: boolean } = {},
): { label: string; kind: PillKind } {
  if (opts.isExpired || statusId === RIDES_UPLOAD_STATUS.Expired) return { label: 'Expired', kind: 'bad' };
  switch (statusId) {
    case RIDES_UPLOAD_STATUS.Accepted:
      return { label: 'Accepted', kind: 'ok' };
    case RIDES_UPLOAD_STATUS.Submitted:
      return { label: 'Waiting for check', kind: opts.approvalRequired ? 'warn' : 'info' };
    case RIDES_UPLOAD_STATUS.Rejected:
      return { label: 'Rejected', kind: 'bad' };
    default:
      return opts.required ? { label: 'Required', kind: 'bad' } : { label: 'Optional', kind: 'info' };
  }
}

/** docs.rejected with the reviewer's note (falls back to a neutral reason). */
export function rejectedCopy(template: string, reason: string | null | undefined): string {
  const r = reason?.trim() ? reason.trim().replace(/([^.!?])$/, '$1.') : 'it was unclear or not the right document.';
  return fillPlaceholders(template, { reason: r });
}

/** Known server-side business errors surfaced in the response envelope. */
const BUSINESS_MESSAGES: Record<string, string> = {
  seats_unavailable: 'Sorry, those seats were just taken. Please pick another driver.',
  document_in_use: ruleCodeCopy(RIDES_RULE_CODES.DocumentInUse),
  booker_not_verified: 'Please do your ID check before you book.',
  guardian_consent_required: 'A parent or guardian must give consent before a child can ride.',
  driver_not_verified: 'Your ID and licence must both be checked before you can offer lifts.',
  driver_suspended: 'Your driver account is suspended. Please contact the church office.',
  same_campus: 'Lifts go between two different campuses. Please choose a different drop-off campus.',
  campus_inactive: 'That campus is not taking rides yet.',
  booking_cutoff: 'Bookings for this ride have closed.',
  minor_requires_group: 'A child must travel with at least one other person. Please go back and add the people travelling with them.',
  female_driver_all_male_party: "A female driver can't take a group with no women. Please choose another driver.",
  event_not_in_rides: 'That event is not open for rides.',
  not_found: 'We could not find that. It may have been removed.',
  [RIDES_ERROR_CODES.DriverPhotoRequired]: DEFAULT_COPY['photo.driver_required'],
  // 254g — the server sends the (admin-editable) copy text as the message; these are the fallbacks.
  [RIDES_ERROR_CODES.DocumentsRequired]: 'Please upload the documents marked "Required" before you continue.',
  [RIDES_ERROR_CODES.DocumentInvalid]: "We couldn't accept that file. It may be too big, damaged or the wrong kind of file. Please try a clear photo (JPG or PNG) or a PDF where allowed.",
  [RIDES_ERROR_CODES.DocumentStorageUnavailable]: "Document uploads aren't available right now. Please try again later, or bring the document to campus security.",
  [RIDES_ERROR_CODES.DeclarationRequired]: DEFAULT_COPY['driver.declaration_required'],
  [RIDES_ERROR_CODES.LicenceExperienceRequired]: "Your licence is too new for Carpool's minimum driving experience.",
  [RIDES_ERROR_CODES.EmergencyContactRequired]: DEFAULT_COPY['emergency.required'],
  [RIDES_ERROR_CODES.DriverNotClearedForMinors]: DEFAULT_COPY['booking.clearance_required'],
  sex_locked: DEFAULT_COPY['profile.sex_locked'],
};

/**
 * Codes whose server message is the member-facing copy (filled with names / years), so it wins
 * over the static fallback above. The fallback is used when the server sent no sentence.
 */
const SERVER_MESSAGE_CODES: string[] = [
  'sex_locked',
  RIDES_ERROR_CODES.DriverPhotoRequired,
  RIDES_ERROR_CODES.DocumentsRequired,
  RIDES_ERROR_CODES.DocumentInvalid,
  RIDES_ERROR_CODES.DocumentStorageUnavailable,
  RIDES_ERROR_CODES.DeclarationRequired,
  RIDES_ERROR_CODES.LicenceExperienceRequired,
  RIDES_ERROR_CODES.EmergencyContactRequired,
  RIDES_ERROR_CODES.DriverNotClearedForMinors,
];

/** A server message a member can read (not a bare code, not a transport error). */
function isSentence(text: string | undefined | null): text is string {
  if (!text) return false;
  const t = text.trim();
  if (t.length < 8 || t.length > 400) return false;
  if (/^[a-z0-9_]+$/i.test(t)) return false; // bare code
  if (/request failed|status code|failed to fetch|network|json|ResponseData/i.test(t)) return false;
  return true;
}

/** Every string an error carries (message, envelope errors[], raw envelope), trimmed + lower-cased. */
function errorStrings(err: unknown): string[] {
  if (!err) return [];
  const e = err as Partial<CCMSError> & { message?: string; code?: string; response?: { data?: { message?: string; errors?: unknown } } };
  const out: unknown[] = [e.message, e.code, e.response?.data?.message];
  if (Array.isArray(e.errors)) out.push(...e.errors);
  if (Array.isArray(e.response?.data?.errors)) out.push(...(e.response!.data!.errors as unknown[]));
  const raw = (e as { raw?: { message?: string; errors?: unknown } }).raw;
  if (raw?.message) out.push(raw.message);
  if (Array.isArray(raw?.errors)) out.push(...(raw!.errors as unknown[]));
  return out.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim().toLowerCase());
}

/** True when the API answered with this business code (RIDES_ERROR_CODES / errors[0]). */
export function hasErrorCode(err: unknown, code: string): boolean {
  const want = code.toLowerCase();
  return errorStrings(err).some((s) => s === want || s.includes(want));
}

/** Turn any thrown error into a sentence a member can act on. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const e = err as Partial<CCMSError> & { message?: string; response?: { data?: { message?: string } } };
  const preferred = SERVER_MESSAGE_CODES.find((code) => hasErrorCode(err, code));
  if (preferred) {
    const serverText = [e.message, e.response?.data?.message, (e as { raw?: { message?: string } }).raw?.message].find(isSentence);
    return serverText ? serverText.trim() : BUSINESS_MESSAGES[preferred];
  }
  const candidates: string[] = [];
  if (typeof e.message === 'string') candidates.push(e.message);
  if (Array.isArray(e.errors)) candidates.push(...e.errors.filter((x): x is string => typeof x === 'string'));
  if (e.response?.data?.message) candidates.push(e.response.data.message);
  const raw = (e as { raw?: { message?: string; errors?: string[] } }).raw;
  if (raw?.message) candidates.push(raw.message);
  if (Array.isArray(raw?.errors)) candidates.push(...raw.errors);

  for (const c of candidates) {
    const key = c.trim().toLowerCase();
    if (BUSINESS_MESSAGES[key]) return BUSINESS_MESSAGES[key];
    const rule = (Object.values(RIDES_RULE_CODES) as string[]).find((code) => key === code || key.includes(code));
    if (rule) return ruleCodeCopy(rule);
    for (const [k, v] of Object.entries(BUSINESS_MESSAGES)) {
      if (key.includes(k)) return v;
    }
  }

  if (e.status === 401) return 'Your session has expired. Please sign in again.';
  if (e.status === 403) return "You don't have access to that.";
  if (e.status === 404) return BUSINESS_MESSAGES.not_found;
  if (e.status === 409) return candidates[0] || 'That clashes with something that already exists.';
  if (e.status && e.status >= 500) return 'Something went wrong on our side. Please try again in a few minutes.';
  if (typeof e.message === 'string' && /fetch|network|Failed to fetch|ECONN/i.test(e.message)) {
    return "We couldn't reach Carpool. Check your internet connection and try again.";
  }
  const first = candidates.find((c) => c && c.length < 200);
  return first || fallback;
}

/** True for a 404 / "not found" style error (used to treat missing profiles as empty). */
export function isNotFoundError(err: unknown): boolean {
  const e = err as Partial<CCMSError> & { message?: string };
  if (e?.status === 404) return true;
  return typeof e?.message === 'string' && /not found/i.test(e.message);
}

/** Static privacy notice used when settings.privacyNoticeHtml is empty (from the prototype). */
export const DEFAULT_PRIVACY_NOTICE_HTML = `
<p>This notice explains how <b>Acts Church</b> handles your personal information under the Protection of Personal Information Act (POPIA). Acts Church is the <b>responsible party</b>.</p>
<p><b>Information Officer:</b> Acts Church · <a href="mailto:info@actschurch.co.za">info@actschurch.co.za</a> · +27 11 805 2381 · 241 Pretorius Road, Vorna Valley, Midrand.</p>
<h3>Why we collect it <small>Purpose · s13</small></h3>
<p>Only to coordinate shared rides to Acts Church services and events, and to verify identities so drivers and riders are safe. We don't use it for anything else or sell it.</p>
<h3>What we collect <small>Minimality · s10</small></h3>
<ul>
<li>Your name and mobile number, and your role (driver or rider).</li>
<li>For drivers: vehicle make, colour, number plate and open seats.</li>
<li>An identity-verification <b>result</b>: a pass/fail flag and a reference. Never the document number itself: only a one-way fingerprint of it.</li>
</ul>
<p>Pick-ups are limited to fixed church campuses; we don't collect your home address.</p>
<h3>Your ID and verification <small>Special info · s26 to 27</small></h3>
<p>Your ID, passport or licence is checked for internal consistency and then confirmed in person by campus security or an admin. We keep only the pass/fail result, <b>never a copy of your document, its number or any biometric data</b>.</p>
<h3>Children under 18 <small>Children · s34 to 35</small></h3>
<p>A rider under 18 is a <b>child</b> under POPIA. We process a child's information only with the <b>prior consent of a competent person</b> (a parent or legal guardian) and collect the minimum needed for the ride.</p>
<h3>Who can see it <small>Openness · s18</small></h3>
<p>A rider sees the driver's name and the vehicle's make, colour and number plate so they can identify the car, <b>not</b> the driver's contact number. Campus security verifies the driver's ID at pickup and drop-off. Giving your details is voluntary, but without them we can't offer you a verified ride.</p>
<h3>How long we keep it <small>Retention · s14</small></h3>
<p>Only as long as needed for the ride and for church records required by law, after which it's deleted or de-identified.</p>
<h3>Security <small>Safeguards · s19</small></h3>
<p>Your information is stored securely with access limited to those who need it. Any security compromise is reported to you and the Information Regulator as s22 requires.</p>
<h3>Your rights <small>Participation · s23 to 25</small></h3>
<p>You can ask to see your information, correct or delete it, object to processing or withdraw consent at any time. You may also complain to the Information Regulator (<a href="mailto:enquiries@inforegulator.org.za">enquiries@inforegulator.org.za</a>).</p>
`;

/**
 * Shown before an SA ID is sent when its date of birth differs from the profile: the ID is the more reliable
 * source (the date is part of the checksummed number), so the profile is corrected from it instead of blocking.
 */
export function dobCorrectionNotice(profileDob: string, idDob: string, whose: 'your' | "the guardian's" = 'your'): string {
  const profile = whose === 'your' ? 'Your profile' : 'The profile';
  const id = whose === 'your' ? 'your ID' : 'the ID';
  return `${profile} says ${profileDob}, but ${id} says ${idDob}. When you send it, we'll update the profile to match ${id}.`;
}
